import React from 'react';
import {
	Box,
	Grid,
	FormControlLabel,
	Checkbox,
	TextField,
	Typography,
	Link,
	Divider,
	Switch,
} from '@mui/material';
import { InStoreEndTextRow } from 'common/types';
import { useForm, Controller } from 'react-hook-form';

import { api } from 'common/frontend/api';

import ContainerBox from 'components/ContainerBox';
import CompletedViewPreview from 'components/CompletedViewPreview';

import CopyableText from 'components/CopyableText';
import { useTranslation } from 'services/localization';

import { useSelector, useDispatch } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import { getLocationEndTextRows, getEmptyEndTextRow } from './utils';
import { updateLocaleField } from 'common/utils/languageUtils';
import { Prompt, Redirect } from 'react-router-dom';
import SaveButton from 'common/components/SaveButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import CheckInPromoPage from 'components/features/promos/CheckInPromoPage';
import HasFeature from 'components/features/HasFeature';
import { useHasFeature } from 'hooks';
import { Routes } from 'routing';
import { getCheckinUrl } from 'common/modules/urls';
import { getClientEnv } from 'common/utils/frontUtils';

type FormValues = {
	customEndText: {
		firstRow: InStoreEndTextRow;
		secondRow: InStoreEndTextRow;
	};
	disableCheckinAvailabilities: boolean;
};

const CheckInTab = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { hasFeature } = useHasFeature();

	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const mainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const isMainLocation = activeLocationId === mainLocationId;

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const shopUrls = useSelector(ShopSelectors.activeShopUrls);
	const isMultiLocationStore = Object.keys(shopUrls?.locationUrls ?? {}).length;
	const showDisableAvailabilitiesSetting = hasFeature('DISABLE_CHECK_IN_AVAILABILITIES');

	const shopInStoreEndText = shopPublicInfo?.inStoreEndText;
	const locationInStoreEndText = getLocationEndTextRows({
		inStoreEndText: shopInStoreEndText,
		locationId: activeLocationId,
		isMainLocation,
	});

	const checkinUrl = getCheckinUrl({
		env: getClientEnv(),
		locationId: isMultiLocationStore ? activeLocationId : null,
		shopUrlDoc: shopUrls,
	});

	const form = useForm<FormValues>({
		defaultValues: {
			customEndText: {
				firstRow: locationInStoreEndText?.[0] ?? getEmptyEndTextRow(false),
				secondRow: locationInStoreEndText?.[1] ?? getEmptyEndTextRow(true),
			},
			disableCheckinAvailabilities: !!shopPublicInfo?.disableCheckinAvailabilities,
		},
		mode: 'onChange',
	});
	const { isSubmitting, dirtyFields } = form.formState;
	const customEndText = form.watch('customEndText');
	//Workaround for formState.isDirty as it internally requires that all defaultValues have registered inputs which we don't have
	const isDirty = !!Object.keys(dirtyFields).length;

	const handleSubmit = async (data: FormValues) => {
		try {
			const customEndTextData = [data?.customEndText?.firstRow, data?.customEndText?.secondRow];
			const customEndTextPath = isMainLocation
				? `inStoreEndText.defaultValue`
				: `inStoreEndText.location.${activeLocationId}`;

			await api()
				.shops.doc(shopId)
				.public.update({
					...(!!dirtyFields.customEndText && { [customEndTextPath]: customEndTextData }),
					...(!!dirtyFields.disableCheckinAvailabilities && {
						disableCheckinAvailabilities: data.disableCheckinAvailabilities,
					}),
				});
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<HasFeature requiredFeature="CHECK_IN" scope="user" fallback={<Redirect to={Routes.STORE} />}>
			<Box>
				<SetPageTitle
					title={t('store.storeSettings', 'Store settings')}
					priority={2}
					rightContent={
						<SaveButton
							disabled={!isDirty}
							loading={isSubmitting}
							onClick={form.handleSubmit(handleSubmit)}
							t={t}
						/>
					}
				/>
				<Prompt
					when={isDirty}
					message={() =>
						t(
							'common:prompts.unsavedChanges',
							'You have unsaved changes - are you sure you want to leave this page?',
						)
					}
				/>
				<HasFeature scope="shop" requiredFeature="CHECK_IN" fallback={<CheckInPromoPage />}>
					<ContainerBox>
						<Box mb={4}>
							<Box>
								<Box
									display="flex"
									flexDirection="row"
									justifyContent="space-between"
									alignItems="flex-start"
								>
									<Typography variant="body1" gutterBottom>
										{t('common:words.url', 'URL')}
									</Typography>
									<Link href={checkinUrl} target="_blank">
										{t('channels.urls.viewStore', 'View store')}
									</Link>
								</Box>
								<CopyableText text={checkinUrl} />
							</Box>
						</Box>
						{showDisableAvailabilitiesSetting && (
							<>
								<Box mt={2}>
									<Grid container>
										<Grid item xs={12} md={6}>
											<Typography variant="h6">
												{t('channels.checkIn.availabilities.title', 'Availabilities')}
											</Typography>
										</Grid>
										<Grid item xs={12} md={6}>
											<Controller
												control={form.control}
												name="disableCheckinAvailabilities"
												render={(renderProps) => (
													<FormControlLabel
														style={{ alignItems: 'center' }}
														control={
															<Switch
																color="primary"
																checked={!renderProps.field.value}
																onChange={(e) => renderProps.field.onChange(!e.target.checked)}
															/>
														}
														label={t(
															'channels.checkIn.availabilities.label',
															'Show availabilities in check-in',
														)}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Box>
								<Box my={4}>
									<Divider />
								</Box>
							</>
						)}
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">
									{t('channels.checkIn.customizeEndScreen', 'Customize end screen')}
								</Typography>
								<Typography variant="body2" gutterBottom>
									{t(
										'channels.checkIn.customizeEndScreenDescription',
										'Customize the content shown to your customers at the end of the check-in flow',
									)}
								</Typography>
								<Box mt={2} />
								<Controller
									control={form.control}
									name={'customEndText.firstRow'}
									render={(renderProps) => (
										<TextField
											value={renderProps.field.value.text.def}
											placeholder={t(
												'shop.nextSteps',
												'Next you can go to the desk and pick up the rental equipment',
											)}
											onChange={(e) => {
												renderProps.field.onChange({
													...renderProps.field.value,
													text: updateLocaleField(
														renderProps.field.value.text,
														e.target.value,
														shopDefaultLanguage,
													),
												});
											}}
											label={t('shop.firstRow', 'First row')}
											variant="outlined"
											fullWidth
										/>
									)}
								/>
								<Box mt={2} />
								<Controller
									control={form.control}
									name={'customEndText.secondRow'}
									render={(renderProps) => (
										<>
											<TextField
												value={renderProps.field.value.text.def}
												placeholder={t(
													'infoText',
													'Rental time starts only when you have picked up all the equipment',
												)}
												onChange={(e) => {
													renderProps.field.onChange({
														...renderProps.field.value,
														text: updateLocaleField(
															renderProps.field.value.text,
															e.target.value,
															shopDefaultLanguage,
														),
													});
												}}
												label={t('shop.secondRow', 'Second row')}
												variant="outlined"
												multiline
												rows={3}
												maxRows={3}
												fullWidth
											/>
											<Box p={2}>
												<FormControlLabel
													control={
														<Checkbox
															checked={renderProps.field.value.helpText}
															onChange={(e) =>
																renderProps.field.onChange({
																	...renderProps.field.value,
																	helpText: e.target.checked,
																})
															}
															name="checkedA"
															color="primary"
														/>
													}
													label={t('shop.smallText', 'Small text')}
												/>
											</Box>
										</>
									)}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<CompletedViewPreview
									thankYouText={`${t('common:greetings.thanks', 'Thank you')}!`}
									nextStepsText={
										customEndText?.firstRow.text.def ||
										t(
											'shop.nextSteps',
											'Next you can go to the desk and pick up the rental equipment',
										)
									}
									infoText={
										customEndText?.secondRow.text.def ||
										t(
											'shop.infoText',
											'Rental time starts only when you have picked up all the equipment',
										)
									}
									isHelpText={customEndText?.secondRow.helpText ?? true}
									endBtnText={t('common:greetings.okBtn')}
									buttonSize={64}
									scale={0.45}
									button="expandButton"
								/>
							</Grid>
						</Grid>
					</ContainerBox>
				</HasFeature>
			</Box>
		</HasFeature>
	);
};

export default CheckInTab;
